import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useState } from 'react';
import { useMutation } from 'react-query';

import { sendEvent } from 'lib/gtag';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import { post } from 'lib/request';

import { useUser } from 'providers/UserProvider';

const LocalWishlistContext = createContext({});

LocalWishlistContext.displayName = 'LocalWishlistContext';

const useLocalWishlist = () => useContext(LocalWishlistContext);

// BACKLOG
// TODO: polymorphism can be applied to this whole hook, way
// too much duplicated stuff, lots of similar logic

const postAddLocalOfferKeywords = (keywords) =>
  post('/wishlist/offer/keyword/bulk', { body: { keywords } });

const postAddLocalCouponKeywords = (keywords) =>
  post('/wishlist/coupon/keyword/bulk', { body: { keywords } });

const LocalWishlistProvider = ({ children }) => {
  const { isLoggedIn } = useUser();
  const [localOfferKeywords, setLocalOfferKeywords] = useState(
    getFromLocalStorage('wishlist.pending_keywords')
      ? getFromLocalStorage('wishlist.pending_keywords').split(',')
      : []
  );
  const [localCouponKeywords, setLocalCouponKeywords] = useState(async () => {
    if (getFromLocalStorage('wishlist.pending_coupons')) {
      const { parseLegacyLocalStorageCouponKeywordsObj } = await import(
        'lib/wishlist'
      );

      return parseLegacyLocalStorageCouponKeywordsObj(
        JSON.parse(getFromLocalStorage('wishlist.pending_coupons'))
      );
    }

    return [];
  });
  const [mutateAddLocalOfferKeywords] = useMutation(postAddLocalOfferKeywords, {
    onMutate: () => {
      removeFromLocalStorage('wishlist.pending_keywords');
      setLocalOfferKeywords([]);
    },
    onSuccess: () => {
      sendEvent({
        action: 'temporary_keyword_added',
        category: 'login_modal_v4',
      });
    },
  });
  const [mutateAddLocalCouponKeywords] = useMutation(
    postAddLocalCouponKeywords,
    {
      onMutate: () => {
        removeFromLocalStorage('wishlist.pending_coupons');
        setLocalCouponKeywords([]);
      },
    }
  );

  const addLocalOfferKeyword = useCallback(
    (keyword) => {
      const isKeywordDuplicate =
        localOfferKeywords.length > 0 &&
        localOfferKeywords.some(
          (localOfferKeyword) => localOfferKeyword === keyword
        );

      if (isKeywordDuplicate) {
        throw new Error('already');
      }

      const newKeywords = localOfferKeywords
        ? [keyword, ...localOfferKeywords]
        : keyword;

      setLocalStorage('wishlist.pending_keywords', newKeywords.toString());
      setLocalOfferKeywords(newKeywords);

      return keyword;
    },
    [localOfferKeywords]
  );

  const removeLocalOfferKeyword = useCallback(
    (keyword) => {
      const newKeywords = localOfferKeywords
        ? localOfferKeywords.filter(
            (storageKeywords) => storageKeywords !== keyword
          )
        : [];

      setLocalStorage('wishlist.pending_keywords', newKeywords.toString());
      setLocalOfferKeywords(newKeywords);

      return newKeywords;
    },
    [localOfferKeywords]
  );

  const sendLocalOfferKeywordsToServer = useCallback(async () => {
    if (!getFromLocalStorage('wishlist.pending_keywords')) {
      return;
    }

    await mutateAddLocalOfferKeywords(
      getFromLocalStorage('wishlist.pending_keywords').split(',')
    );
  }, []);

  const addLocalCouponKeyword = useCallback(
    async (keyword) => {
      if (!keyword) {
        throw new Error('INVALID_KEYWORD');
      }

      const isKeywordDuplicate =
        localCouponKeywords.length > 0 &&
        localCouponKeywords.some(
          ({ keywordDisplay }) => keywordDisplay === keyword.keywordDisplay
        );

      if (isKeywordDuplicate) {
        /**
         * This is the same kind of error that the API returns, we're mimicking
         * it here so we can handle it the same way
         */
        throw new Error('already');
      }

      const newKeywords = localCouponKeywords
        ? [keyword, ...localCouponKeywords]
        : keyword;

      const { toLegacyLocalStorageCouponKeywordsObj } = await import(
        'lib/wishlist'
      );

      setLocalStorage(
        'wishlist.pending_coupons',
        JSON.stringify(toLegacyLocalStorageCouponKeywordsObj(newKeywords))
      );
      setLocalCouponKeywords(newKeywords);

      return keyword;
    },
    [localCouponKeywords]
  );

  const removeLocalCouponKeyword = useCallback(
    async (keyword) => {
      const newKeywords = localCouponKeywords
        ? localCouponKeywords.filter(
            ({ keywordDisplay }) => keywordDisplay !== keyword.keywordDisplay
          )
        : [];

      const { toLegacyLocalStorageCouponKeywordsObj } = await import(
        'lib/wishlist'
      );

      setLocalStorage(
        'wishlist.pending_coupons',
        JSON.stringify(toLegacyLocalStorageCouponKeywordsObj(newKeywords))
      );
      setLocalCouponKeywords(newKeywords);

      return newKeywords;
    },
    [localCouponKeywords]
  );

  const sendLocalCouponKeywordsToServer = useCallback(async () => {
    if (!getFromLocalStorage('wishlist.pending_coupons')) {
      return;
    }

    const [{ parseLegacyLocalStorageCouponKeywordsObj }, { pick }] =
      await Promise.all([import('lib/wishlist'), import('lib/object')]);

    await mutateAddLocalCouponKeywords(
      parseLegacyLocalStorageCouponKeywordsObj(
        JSON.parse(getFromLocalStorage('wishlist.pending_coupons'))
      ).map((localCouponKeyword) =>
        pick(localCouponKeyword, ['keywordCategoryId', 'keywordStoreId'])
      )
    );
  }, []);

  const withLocalWishlistItems = useCallback(
    (wishlist) => {
      const newWishlist = wishlist;

      if (!isLoggedIn && localOfferKeywords.length > 0) {
        // Fill items infos using offer keywords from localStorage
        newWishlist.items = localOfferKeywords.map((offerKeyword) => ({
          keywordDisplay: offerKeyword,
          keywordMax: -1,
          keywordMin: 0,
          keywordCategoryId: 0,
          keywordTop: false,
        }));
        newWishlist.suggestions = wishlist.suggestions.filter((suggestion) =>
          localOfferKeywords.some(
            (localOfferKeyword) => localOfferKeyword !== suggestion
          )
        );
      }

      return newWishlist;
    },
    [localOfferKeywords]
  );

  return (
    <LocalWishlistContext.Provider
      value={{
        localOfferKeywords,
        addLocalOfferKeyword,
        removeLocalOfferKeyword,
        sendLocalOfferKeywordsToServer,
        localCouponKeywords,
        addLocalCouponKeyword,
        removeLocalCouponKeyword,
        sendLocalCouponKeywordsToServer,
        withLocalWishlistItems,
      }}
    >
      {children}
    </LocalWishlistContext.Provider>
  );
};

LocalWishlistProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LocalWishlistProvider;

export { useLocalWishlist };
